/**
 * Helpers for league schedule requests.
 *
 * Short rosters (LG-B22): the server refuses to schedule teams below the
 * sport's minimum with 409 { details.code: 'SHORT_ROSTERS' }. The host is
 * asked once and the request is sent again with allow_short_rosters.
 *
 * Shortfall (LG-B04): when the season cannot hold every game the server says
 * how many do not fit; the message shows a few of them.
 */

export const isShortRosters = (error) =>
  error?.response?.status === 409 && error.response.data?.details?.code === 'SHORT_ROSTERS';

/**
 * Send a schedule request; on a short-roster refusal ask `confirm(message)`
 * and retry with allow_short_rosters. Resolves to the response, or null when
 * the host declined.
 * @param {(extra: Object) => Promise} send  builds and sends the request with extra body fields
 * @param {(message: string) => boolean|Promise<boolean>} [confirm]
 */
export async function sendWithRosterGoAhead(send, confirm = (m) => window.confirm(m)) {
  try {
    return await send({});
  } catch (error) {
    if (!isShortRosters(error)) {
      throw error;
    }
    const ok = await confirm(`${error.response.data.message}\n\nSchedule anyway?`);
    if (!ok) {
      return null;
    }
    return send({ allow_short_rosters: true });
  }
}

/** A readable message for a failed schedule request, with the games that do not fit. */
export function scheduleErrorMessage(error) {
  const data = error?.response?.data || {};
  const message = data.message || error?.message || 'Request failed';
  const unscheduled = data.details?.unscheduled;
  if (Array.isArray(unscheduled) && unscheduled.length) {
    const sample = unscheduled.slice(0, 3).map((g) => g.label).join(', ');
    return `${message} Not scheduled: ${sample}${unscheduled.length > 3 ? ` and ${unscheduled.length - 3} more` : ''}.`;
  }
  if (Array.isArray(data.errors) && data.errors.length > 1) {
    return `${message} (${data.errors.length} problems)`;
  }
  return message;
}
